import dayjs, { extend, locale } from 'dayjs';
import type { Dayjs } from 'dayjs';
import advanced from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isBetween from 'dayjs/plugin/isBetween';
import isToday from 'dayjs/plugin/isToday';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import invariant from 'tiny-invariant';
import type { QueryParamConfig } from 'use-query-params';

import type { DateRange } from '@packages/fenrir/components';
import { TIMEZONE_LOCAL } from 'ae-constants/constants';

/**
 * TODO
 * Review this file: `extend`s, like i18n, should be done once per application, even if we never import this file.
 * Location: CoreProviders?
 */

import('dayjs/locale/en');

extend(utc);
extend(localizedFormat);
extend(customParseFormat);
extend(isBetween);
extend(isToday);
extend(timezone);
extend(advanced);

const DEFAULT_LANGUAGE_KEY = 'en';

export const setDayjsLocale = () => locale(DEFAULT_LANGUAGE_KEY);

export const parseLocalTime = (hour: string, minute: string) => {
  const localTime = dayjs(`${hour}:${minute}`, 'HH:mm').local();

  return localTime;
};

export const parseLocalToUTC = (day: string, hour: string, minute: string) => {
  return dayjs()
    .date(parseInt(day, 10))
    .minute(parseInt(minute, 10))
    .hour(parseInt(hour, 10))
    .utc();
};

export type Rollover = {
  isRollover: boolean; // is the day rolled over
  rolledBack: boolean; // is the day rolling back
};

export const parseWeeklyLocalToUTC = (
  day: string,
  hour: string,
  minute: string,
) => {
  return dayjs()
    .day(parseInt(day, 10))
    .minute(parseInt(minute, 10))
    .hour(parseInt(hour, 10))
    .utc();
};

// check for rollover on the day, weekly/monthly schedules. Input is either Local or the passed in tz, compares against UTC
export const isDayRollover = (
  day: string,
  hour: string,
  minute: string,
  tz?: string,
): Rollover => {
  let date = dayjs();

  if (tz) {
    date = date.tz(tz);
  }

  date = date
    .day(parseInt(day, 10))
    .minute(parseInt(minute, 10))
    .hour(parseInt(hour, 10));
  return {
    isRollover: date.day() !== date.utc().day(),
    rolledBack: date.day() > date.utc().day(),
  };
};

// check for rollover on the day, weekly/monthly schedules. Input is utc time, compares against local
export const isDayRolloverUTC = (
  day: string,
  hour: string,
  minute: string,
): Rollover => {
  const dateUtc = dayjs()
    .utc()
    .day(parseInt(day, 10))
    .minute(parseInt(minute, 10))
    .hour(parseInt(hour, 10));
  return {
    isRollover: dateUtc.local().day() !== dateUtc.day(),
    rolledBack: dateUtc.local().day() < dateUtc.day(),
  };
};

export const getDayJsDefaultMonth = () => {
  const currentDate = dayjs();
  const jan = currentDate.month(0);
  if (currentDate.utcOffset() !== jan.utcOffset()) {
    return 4; // return may as default
  }
  return 0; // return jan
};

export const parseTime = (
  hour: string,
  minute: string,
  tz: string,
  day?: string,
) => {
  day = day || dayjs().utc().date().toString();
  if (tz === TIMEZONE_LOCAL) {
    // Added month to parse date to get 31 days for date selection
    const time = `0${getDayJsDefaultMonth() + 1} ${day}:${hour}:${minute}`;
    const format = 'MM D:HH:mm';
    return dayjs(time, format);
  }
  return dayjs()
    .utc()
    .month(getDayJsDefaultMonth())
    .date(parseInt(day, 10))
    .minute(parseInt(minute, 10))
    .hour(parseInt(hour, 10));
};

export const parseUTCTime = (hour: string, minute: string) => {
  const parsedUTCTime = dayjs.utc(`${hour}:${minute}`, 'HH:mm');
  return parsedUTCTime;
};

export const parseUTCToLocal = (hour: string, minute: string) => {
  const parsedTime = dayjs.utc(`${hour}:${minute}`, 'HH:mm');

  return parsedTime.local();
};

const DATE_FORMAT = 'YYYY-MM-DDTHH:mm:ssZZ';

export const formatUriDate = (date: Dayjs | undefined) =>
  encodeURIComponent(dayjs(date).format(DATE_FORMAT));

const validDateOrNull = (dateString?: string | null) => {
  if (!dateString) return null;

  const date = dayjs(dateString);
  if (!date.isValid()) return null;

  return date;
};

export const DayJsParam: QueryParamConfig<Dayjs | null> = {
  decode: (dateString) => {
    if (Array.isArray(dateString)) {
      return dateString.length > 0 ? validDateOrNull(dateString[0]) : null;
    }
    return validDateOrNull(dateString);
  },
  encode: (dayjsInstance) => {
    if (dayjsInstance == null || !dayjsInstance.isValid()) {
      return undefined;
    }
    return dayjs(dayjsInstance).utc().toISOString();
  },
};

export const DayJsRangeParam: QueryParamConfig<DateRange<Dayjs> | null> = {
  decode: (range) => {
    if (Array.isArray(range) && range.length === 2) {
      const start = validDateOrNull(range[0]);
      const end = validDateOrNull(range[1]);

      return start && end ? [start, end] : null;
    }
    return null;
  },
  encode: (range) => {
    const [start, end] = range || [];

    if (!start?.isValid() || !end?.isValid()) return undefined;

    return [dayjs(start).utc().toISOString(), dayjs(end).utc().toISOString()];
  },
};

export const RequiredDayJsParam: QueryParamConfig<Dayjs> = {
  decode: (dateString) => {
    invariant(dateString);
    invariant(!Array.isArray(dateString));
    const date = dayjs(dateString);
    invariant(date.isValid());
    return date;
  },

  encode: (dayjsInstance) => {
    invariant(dayjsInstance.isValid());
    return dayjs(dayjsInstance).utc().toISOString();
  },
};

export { dayjs };
